<script>
  import { createEventDispatcher, onMount } from 'svelte';

  import Input from '@components/Input.svelte';
  import Button from '@components/Button.svelte';
  import Icon from '@components/Icon.svelte';
  import { EmailIconSvg } from '@utils/icons';
  import Auth from '@utils/auth';
  import Notification from '@utils/notification';
  import BrowserUtils from '@utils/browser-utils';
  import Log from '@utils/log';
  import UserAjax from '@ajax/user';
  import OAuthAjax from '@ajax/oauth';
  import AppInviteAjax from '@ajax/app_invite';
  import TermsAndPrivacyAjax from '@ajax/terms_and_privacy';
  

  import EmailInput from '@app-components/EmailInput.svelte';
  import PasswordInput from '@app-components/PasswordInput.svelte';
  import Cookie from 'js-cookie';
  import { goto } from '$app/navigation';
  import jQuery from 'jquery';
  import { getCurrentUserStore } from '@stores/current_user';
  import { browser } from '$app/environment';
  import queryString from 'query-string';
  import Track from '@utils/track';

  const dispatch = createEventDispatcher();

  export let mobile = false;
  export let successCallback;
  export let afterLoginCallback;
  export let showPassword = true;
  export let signInView = false;
  export let showEmailForm = false
  export let showActionLink = true;
  export let showAccountCreateButton = false;
  export let namePlaceholder = 'Full name';
  export let header;
  export let showOAuthButtons = true;
  export let showOAuthButtonsVisually = true; // used to hide the buttons visually, but still render them in the DOM
  export let inviteFlow = false;
  export let closeModal;
  export let saveButtonFlow = false;
  export let customButtonSignupText;
  export let prefilledName;
  export let prefilledEmail;

  let emailValid = typeof location !== 'undefined' && queryString.parse(location.search).email ? true : false;
  let noAccountError = false;
  let currentUser = getCurrentUserStore('default');

  $: signInFormValid = email?.length && emailValid && signInPassword?.length;
  $: createFormValid = email?.length && emailValid && (showPassword === false || password?.length) && name?.length;
  $: formValid = signInView ? signInFormValid : createFormValid;

  export let emailValidationFn = async (_emailValue) => {};

  const validateAndCheckIfEmailTaken = async (emailValue) => {
    const error = await emailValidationFn(emailValue);

    if (error) return error;

    // only check for email re-use if the email in the input field matches the
    // email regex / looks like a valid email address.
    let exists = false;
    if (BrowserUtils.emailIsValid(emailValue)) {
      exists = await UserAjax.checkEmail(emailValue);
    }

    return exists ? 'exists' : undefined;
  };

  let defaultSignInView = signInView;

  export let toggleActionLinkView = () => {
    noAccountError = false;
    if (!signInView) {
      showEmailForm = false;
      showAccountCreateButton = false;
    }
    signInView = !signInView;
    showOAuthButtonsVisually = true;
  };

  export let onEmailFormButtonClick = () => {
    showEmailForm = true;
    showAccountCreateButton = true;
    showOAuthButtonsVisually = false;
  };

  export let beforeDataSend;

  let termsAndPrivacyConfig = { terms_url: '', privacy_url: '' };

  let saving = false;
  let name = (typeof location !== 'undefined' && queryString.parse(location.search).name) || prefilledName || '';
  let email = (typeof location !== 'undefined' && queryString.parse(location.search).email) || prefilledEmail || '';
  let password;
  let signInPassword;

  let formContainerEl;

  let showSpinner = false;

  $: headerText = signInView ? 'Sign in' : header || 'Create account';
  $: buttonText = signInView ? 'Sign in' : customButtonSignupText || 'Create account';

  let submitOnEnter = function (e) {
    if (e.detail.which == 13) {
      if (signInView) {
        signIn();
      } else {
        saveAccount();
      }
    }
  };

  let close = function () {
    saving = false;
    signInView = defaultSignInView;

    name = '';
    email = '';
    signInPassword = '';

    closeModal();
  };

  let signIn = async function () {
    saving = true;
    Track.signInClick();

    let emailFromInput = jQuery('#login-email-field input').val();
    let passFromInput = jQuery('#login-password-field input').val();

    try {
      let resp = await Auth.signIn(emailFromInput, passFromInput);
      Log.track(`[SIGN IN] [1] (${emailFromInput}) | resp: ${JSON.stringify(resp)}`);

      let user = await Auth.load(currentUser, resp.token);
      Log.track(`[SIGN IN] [2] (${emailFromInput}) | user: ${user && user.id}`);

      Cookie.set('fs-rec', undefined);

      try {
        afterLoginCallback && (await afterLoginCallback(user));
        await successCallback(user, true);
      } catch (err) {
        Log.track(`[SIGN IN] [3] (${emailFromInput}) | successCallback error: ${JSON.stringify(err)}`);
      }

      close();
    } catch (err) {
      if (err) {
        Notification.send({
          text: "No account found or invalid password. <a href='/forgot_password' style='margin-left: 10px; color: #cabefb;'>Forgot password?</a>",
        });
        noAccountError = true;
      }
    }

    saving = false;
  };

  let getSaveData = function () {
    if (!showPassword) {
      password = Math.random().toString(36).slice(-8);
    }

    let data = {
      user: {
        name: name,
        email: email,
        password: password,
        password_confirmation: password,
        source: Cookie.get('utm_source'),
        utm_medium: Cookie.get('utm_medium'),
        utm_campaign: Cookie.get('utm_campaign'),
        utm_content: Cookie.get('utm_content'),
        external_referrer: Cookie.get('external_referrer'),
      },
    };

    data = beforeDataSend(data);

    return data;
  };

  let saveInvitePendingUserAccount = async function () {
    saving = true;
    let data = getSaveData();

    try {
      let resp = await AppInviteAjax.createPendingUser(data);

      if (resp && resp.code) {
        dispatch('go-to-username-step', resp);
      }
    } catch (err) {
      if (err && err.full_errors && err.full_errors.length > 0) {
        Notification.send({
          text: err.full_errors[0],
        });
      }

      saving = false;
    }
  };

  let saveAccount = async function () {
    if (saving) {
      return;
    }

    Track.createAccountClick();

    if (inviteFlow) {
      saveInvitePendingUserAccount();
      return;
    }

    saving = true;
    let data = getSaveData();

    try {
      let resp = await UserAjax.create(data);

      if (currentUser) {
        await Auth.signout(currentUser, { redirectToRoot: false });
      }

      let user = await Auth.load(currentUser, resp.token);

      if (saveButtonFlow) {
        dispatch('go-to-username-step', resp);
        return;
      }

      Cookie.set('fs-rec', undefined);

      try {
        await successCallback(user);
      } catch {}

      close();
    } catch (err) {
      if (err && err.full_errors && err.full_errors.length > 0) {
        Notification.send({
          text: err.full_errors[0],
        });
      }
    }

    saving = false;
  };

  let handleOauthResponseWithInviteFlow = function (resp) {
    dispatch('go-to-username-step', resp);
  };

  let onGoogleSignIn = async function (response) {
    let token = response.credential;
    try {
      showSpinner = true;

      let resp = await OAuthAjax.signInGoogle(
        beforeDataSend({
          token: token,
          invite_flow: inviteFlow,
        })
      );

      if (resp.type === 'user') {
        let user = await Auth.load(currentUser, resp.token);
        Cookie.set('fs-rec', undefined);
        afterLoginCallback && (await afterLoginCallback(user));
        successCallback(user);
        Track.signedUpWithGoogle();
        close();
      } else {
        handleOauthResponseWithInviteFlow(resp);
      }
    } catch (err) {
      if (err && err.error) {
        Notification.send({
          text: err.error,
        });
      }

      showSpinner = false;
    }
  };

  // chrome on ios doesn't work with autofill password without this fix
  let floatingContainerTimeout;
  let shouldShowFloatingContainer = false;

  let showFloatingContainerPreventScrolling = function (e) {
    e.preventDefault();
  };

  let showFloatingContainer = function () {
    if (shouldShowFloatingContainer) {
      let formContainer = jQuery(formContainerEl);
      clearTimeout(floatingContainerTimeout);
      jQuery(formContainer).addClass('floating-login-container');

      if (window.visualViewport) {
        setTimeout(() => {
          jQuery(formContainer).css('height', window.visualViewport.height);
        }, 100);
      }

      document.body.addEventListener('touchmove', showFloatingContainerPreventScrolling, {
        passive: false,
      });
    }
  };

  let hideFloatingContainer = function () {
    if (shouldShowFloatingContainer) {
      clearTimeout(floatingContainerTimeout);

      floatingContainerTimeout = setTimeout(() => {
        jQuery(formContainerEl).removeClass('floating-login-container').css('height', 'unset');

        document.body.removeEventListener('touchmove', showFloatingContainerPreventScrolling);
      }, 100);
    }
  };

  let triggerMetaPixelTrackInitiateCheckout = () => {
    if (typeof window !== 'undefined' && window.fbq !== undefined) {
      window.fbq('track', 'InitiateCheckout');
    }
  };

  let handleEmailSignIn = function () {
    Track.signedUpWithEmail();
    onEmailFormButtonClick();
  };

  let handleNameBlur = function () {
    if (name) {
      Track.fullNameEntered({ value: name });
    }
  };

  let handleEmailBlur = function () {
    if (email && emailValid) {
      Track.emailEntered({ email });
    }
  };

  let handlePasswordBlur = function () {
    if (password) {
      Track.passwordEntered();
    }
  };

  (async () => {
    try {
      termsAndPrivacyConfig = await TermsAndPrivacyAjax.get();
    } catch {
      Log.info('Unable to load privacy & terms in frontend (in create account)', true);
    }
  })();

  onMount(() => {
    let checkGoogleApi = setInterval(() => {
      if (typeof google !== 'undefined' && google.accounts && google.accounts.id) {
        clearInterval(checkGoogleApi);

        google.accounts.id.initialize({
          client_id: window.GOOGLE_CLIENT_ID,
          callback: onGoogleSignIn,
        });
        google.accounts.id.renderButton(document.getElementById('google-sign-in-btn'), {
          size: 'large',
          width: mobile ? window.innerWidth - 100 : 350,
          shape: 'rectangular',
        });
      }
    }, 100);

    triggerMetaPixelTrackInitiateCheckout();
  });
</script>

<style style="text/scss" lang="scss">
  @import '../../../vendor/design-system.scss';
  * :global(.abcRioButton) {
    width: 100% !important;
    box-shadow: unset !important;
    border: 0px !important;
    border-radius: 12px !important;
  }

  * :global(.abcRioButtonContents) {
    font-weight: bold !important;
    font-size: 15px !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif,
      'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji' !important;
    color: rgba(0, 0, 0, 0.8) !important;
    margin-left: 7px !important;
  }

  * :global(.abcRioButtonContentWrapper) {
    display: flex !important;
    justify-content: center !important;
    margin-left: -9px !important;
  }

  #google-sign-in-btn {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .sign-in-and-inputs-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
    padding: 0 40px;

    .sign-up-btn {
      display: flex;
      height: 48px;
      padding: 12px 23px;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 8px;
      align-self: stretch;
      max-width: 350px;
      width: 100%;
      margin: 0 auto;
      background: #fff;
      border-radius: 8px;
      font-weight: 500;

      &:hover {
        background: rgba(66,133,244,.04)
      }
    }
  }

  .account-container {
    margin-top: 60px;

    .content-container {
      flex: 1;
      flex-shrink: 0;
      justify-content: center;
      border-top: 1px solid #ececec;
      overflow: auto;
      padding-top: 24px;

      .benable-app-logo {
        width: 38px;
        height: 38px;
        border-radius: 8px;
        margin-bottom: 24px
      }

      .header-1 {
        font-size: 34px;
        line-height: 32px;
        font-weight: 800;
        text-transform: uppercase;
        color: #717171;
        margin-bottom: 32px;
      }

      .sign-in-with {
          height: 0px;
          overflow: hidden;
        &.show-oauth-buttons {
          height: auto;
          overflow: visible;
        }


        .sign-in-btn {
          border: 1px solid #ececec;
          border-radius: 12px;
          height: 48px;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          transition: 0.2s all;

          &:hover {
            border: 1px solid #7054dc;
          }
        }
      }
    }

    .terms-and-privacy {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      padding: 0 20px;
      color: #717171;

      a {
        color: #0c80f8;

        &:hover {
          color: #99d5ff;
        }
      }
    }
  }

  .ui-actions {
    margin-top: 24px;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
  }

  .input-label {
    text-align: left;
    font-size: 16px;
    line-height: 24px;
    font-weight: 500;
    margin-bottom: 5px;
  }

  .action-link-container {
    border-top: 1px solid #ececec;
    margin-top: 24px;
    padding: 18px 0;
    text-align: center;
    &.error {
      .top-text {
        color: $red;
      }

      .action-link-bottom.top-link {
        color: $red;
        font-weight: bold;
        &:hover {
          color: $red-dark;
        }
      }
    }
    .top-text {
      font-size: 16px;
      line-height: 20px;
      color: rgba(26, 26, 26, 1);
    }

    .action-link-bottom {
      color: #7A5CFA;
      font-weight: bold;
      font-size: 16px;
      line-height: 20px;
      cursor: pointer;

      &:hover {
        color: #7054dc;
      }
    }
  }

  .mobile {
    &.account-container {
      margin: 40px -20px 0 -20px !important;
    }

    .action-link-container {
      padding-bottom: 0;
    }

    .sign-in-and-inputs-container {
      padding: 0 20px;
    }
    
    .header-1 { padding: 0 20px; }
  }

  :global(.floating-login-container) {
    position: fixed;
    background: white;
    padding: 20px;
    padding-left: 40px;
    padding-right: 40px;
    top: 0px;
    right: 0px;
    left: 0px;
    z-index: 999;
  }
</style>

<svelte:head>
  {#if browser}
    <script src="https://accounts.google.com/gsi/client" async defer></script>
  {/if}
</svelte:head>

<div class="account-container" class:mobile={mobile}>
  <div class="content-container purple-scrollbar" style={mobile ? '' : 'position: relative'}>
    <div>
      <img class="benable-app-logo" src="/images/benable-app-logo.png" />
    </div>
    <div class="header-1 national-2-narrow">{@html headerText}</div>

    {#if showSpinner}
      <div style="position: absolute; left: calc(50% - 16px); top: calc(50% - 16px);">
        <i class="fa fa-spinner fa-spin" style="font-size: 32px; color: #7054dc;" />
      </div>
    {:else}
      <div class="sign-in-and-inputs-container">
        {#if showOAuthButtons}
          <div class="sign-in-with" class:show-oauth-buttons={showOAuthButtonsVisually}>
            <div id="google-sign-in-btn" />
          </div>
        {/if}

        <div class="form-container" bind:this={formContainerEl}>
          {#if signInView}
            <div id="login-email-field">
              <div class="input-label">Email</div>
              <EmailInput
                name="email"
                on:keyup={submitOnEnter}
                bind:value={email}
                focus={!mobile}
                on:focus={showFloatingContainer}
                on:blur={hideFloatingContainer}
                inputProps={{ autocomplete: 'username' }}
                validateFn={emailValidationFn}
                validateAfterTyping={true}
                bind:valid={emailValid}
                inputStyle="font-weight: 400"
                style="width: 275px; margin-left: auto; margin-right: auto;"
                placeholder="Your email address" />
            </div>

            <div style="margin-top: 12px;" id="login-password-field">
              <div class="input-label">Password</div>
              <PasswordInput
                name="password"
                on:focus={showFloatingContainer}
                on:blur={hideFloatingContainer}
                on:keyup={submitOnEnter}
                bind:value={signInPassword}
                inputProps={{ autocomplete: 'current-password' }}
                inputStyle="font-weight: 400"
                style="width: 275px; margin-left: auto; margin-right: auto;" />
            </div>
          {:else}
            {#if showEmailForm}
              <div>
                <div class="input-label">
                  {namePlaceholder}
                </div>
                <Input
                  type="text"
                  focus={!mobile}
                  bind:value={name}
                  inputStyle="font-weight: 400"
                  style="width: 275px; margin-left: auto; margin-right: auto;"
                  on:blur={handleNameBlur} />
              </div>

              <div style="margin-top: 12px;">
                <div class="input-label">Email address</div>
                <EmailInput
                  bind:value={email}
                  validateFn={validateAndCheckIfEmailTaken}
                  validateAfterTyping={true}
                  bind:valid={emailValid}
                  inputStyle="font-weight: 400"
                  style="width: 275px; margin-left: auto; margin-right: auto;"
                  on:keyup={submitOnEnter}
                  on:blur={handleEmailBlur}
                  on:sign-in-clicked={() => {
                    signInView = true;
                  }} />
              </div>

              {#if showPassword}
                <div style="margin-top: 12px;">
                  <div class="input-label">Password</div>
                  <PasswordInput
                    bind:value={password}
                    inputStyle="font-weight: 400"
                    style="width: 275px; margin-left: auto; margin-right: auto;"
                    on:keyup={submitOnEnter}
                    on:blur={handlePasswordBlur} />
                </div>
              {/if}
            {:else}
              <button class="sign-up-btn" on:click={handleEmailSignIn}>
                <Icon size={20} path={EmailIconSvg} /><span>Sign up with email</span>
              </button>
            {/if} 
          {/if}

          {#if showAccountCreateButton || signInView}
            <div class="ui-actions">
              <Button
                colorScheme="purple"
                shadow={false}
                on:click={signInView ? signIn : saveAccount}
                disabled={signInView ? false : !formValid}>
                {#if saving}
                  <div>
                    <i class="fa fa-spin fa-spinner" />
                    <span style="margin-left: 10px;">{signInView ? 'Working' : 'Saving'}</span>
                  </div>
                {:else}{buttonText}{/if}
              </Button>
            </div>
          {/if}
        </div>
      </div>

      {#if signInView == false}
        <div class="terms-and-privacy">
          By signing up, you're agreeing to our <a
            on:click={() => window.open(termsAndPrivacyConfig.terms_url)}
            href={termsAndPrivacyConfig.terms_url}
            target="_blank">terms of service</a> and <a
            on:click={() => window.open(termsAndPrivacyConfig.privacy_url)}
            href={termsAndPrivacyConfig.privacy_url}
            target="_blank">privacy policy</a>.
        </div>
      {/if}

      {#if showActionLink}
        <div class="action-link-container {noAccountError ? 'error' : ''}">
          <span class="top-text">{signInView ? 'Need an account?' : 'Already have an account?'}</span>
          <span class="action-link-bottom top-link" on:click={toggleActionLinkView}
            >{signInView ? 'Create account' : 'Login'}</span>

          {#if signInView}
            <div style="margin-top: 20px;">
              <a on:click={() => goto('/forgot_password')} href="/forgot_password" class="action-link-bottom"
                >Forgot password</a>
            </div>
          {/if}
        </div>
      {/if}
    {/if}
  </div>
</div>
